import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { themeBlue, themeOrange } from "../../utils/colorTheme";
import { apiHandle } from "../../config/apiHandle/apiHandle";
import { MdEmail, MdSend } from "react-icons/md";

const RECIPIENT_OPTIONS = [
  { value: "single", label: "Single recipient (search)" },
  { value: "all_users", label: "All customers" },
  { value: "all_pros", label: "All pros" },
  { value: "signed_pros", label: "Pros who signed agreement" },
  { value: "unsigned_pros", label: "Pros who haven't signed" },
];

const DashboardEmail = () => {
  const [recipientType, setRecipientType] = useState("single");
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [previewCount, setPreviewCount] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiHandle
      .get("/admin/user-listing")
      .then((res) => setAllUsers(res.data?.users || []))
      .catch(() => setAllUsers([]))
      .finally(() => setLoadingUsers(false));
  }, []);

  const userOptions = useMemo(
    () =>
      allUsers
        .filter((u) => u.email)
        .map((u) => ({
          id: u.id,
          label: `${u.first_name || ""} ${u.last_name || ""} — ${u.email} ${
            u.user_role === 2 ? "(Pro)" : u.user_role === 3 ? "(Customer)" : ""
          }`.trim(),
          role: u.user_role,
        })),
    [allUsers]
  );

  const fetchPreview = async () => {
    setResult(null);
    try {
      const body = {
        recipient_type: recipientType,
        user_ids: recipientType === "single" && selectedUser ? [selectedUser.id] : undefined,
      };
      const res = await apiHandle.post("/admin/email/preview", body);
      setPreviewCount(res.data?.count ?? 0);
      return res.data?.count ?? 0;
    } catch (err) {
      setResult({ ok: false, message: err?.response?.data?.message || err.message });
      return null;
    }
  };

  const onClickSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setResult({ ok: false, message: "Subject and body are required." });
      return;
    }
    if (recipientType === "single" && !selectedUser) {
      setResult({ ok: false, message: "Pick a recipient first." });
      return;
    }
    const count = await fetchPreview();
    if (count == null) return;
    if (count === 0) {
      setResult({ ok: false, message: "No recipients matched." });
      return;
    }
    setConfirmOpen(true);
  };

  const doSend = async () => {
    setSending(true);
    setConfirmOpen(false);
    try {
      const payload = {
        recipient_type: recipientType,
        user_ids: recipientType === "single" && selectedUser ? [selectedUser.id] : undefined,
        subject,
        body,
      };
      const res = await apiHandle.post("/admin/email/send", payload);
      setResult({
        ok: true,
        message: `Sent ${res.data?.sent ?? 0} of ${res.data?.attempted ?? 0}${
          res.data?.failed ? ` (${res.data.failed} failed)` : ""
        }`,
        errors: res.data?.errors || [],
      });
      if (!res.data?.failed) {
        setSubject("");
        setBody("");
      }
    } catch (err) {
      setResult({ ok: false, message: err?.response?.data?.message || err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack sx={{ bgcolor: "#F8F9FA", minHeight: "100vh" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2.5 }}>
        <Stack>
          <Typography sx={{ fontSize: "24px", fontWeight: 700, color: themeBlue }}>
            Email
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#999", mt: 0.3 }}>
            Send messages to customers, pros, or specific groups
          </Typography>
        </Stack>
      </Stack>

      <Container sx={{ maxWidth: { xl: "md", lg: "md", md: "md", sm: "sm", xs: "xs" } }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: "14px", p: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          {result && (
            <Alert
              severity={result.ok ? "success" : "error"}
              onClose={() => setResult(null)}
              sx={{ mb: 2 }}
            >
              {result.message}
              {result.errors && result.errors.length > 0 && (
                <Box sx={{ mt: 1, fontSize: 12 }}>
                  {result.errors.map((e, i) => (
                    <div key={i}>• {e}</div>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <Stack>
              <Typography sx={{ fontSize: 13, color: "#666", mb: 0.5, fontWeight: 600 }}>
                Recipients
              </Typography>
              <Select
                size="small"
                value={recipientType}
                onChange={(e) => {
                  setRecipientType(e.target.value);
                  setPreviewCount(null);
                  setSelectedUser(null);
                }}
              >
                {RECIPIENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            {recipientType === "single" && (
              <Stack>
                <Typography sx={{ fontSize: 13, color: "#666", mb: 0.5, fontWeight: 600 }}>
                  Pick recipient
                </Typography>
                {loadingUsers ? (
                  <CircularProgress size={20} sx={{ color: themeOrange }} />
                ) : (
                  <Autocomplete
                    size="small"
                    options={userOptions}
                    value={selectedUser}
                    onChange={(_, v) => setSelectedUser(v)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Search by name or email…" />
                    )}
                  />
                )}
              </Stack>
            )}

            {recipientType !== "single" && (
              <Box sx={{ bgcolor: "#FFF7ED", borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: 13, color: "#7c4a00" }}>
                  Broadcast — recipient count will be confirmed before sending.
                </Typography>
              </Box>
            )}

            <Stack>
              <Typography sx={{ fontSize: 13, color: "#666", mb: 0.5, fontWeight: 600 }}>
                Subject
              </Typography>
              <TextField
                size="small"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject line"
                inputProps={{ maxLength: 255 }}
              />
            </Stack>

            <Stack>
              <Typography sx={{ fontSize: 13, color: "#666", mb: 0.5, fontWeight: 600 }}>
                Body (plain text)
              </Typography>
              <TextField
                multiline
                minRows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your message…"
                inputProps={{ maxLength: 50000 }}
              />
              <Typography sx={{ fontSize: 11, color: "#999", mt: 0.5 }}>
                Sent from no-reply@donerightaway.com — recipients can't reply.
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="flex-end">
              <Button
                onClick={onClickSend}
                disabled={sending}
                variant="contained"
                startIcon={sending ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <MdSend />}
                sx={{
                  background: "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {sending ? "Sending…" : "Send"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm send</DialogTitle>
        <DialogContent>
          <Typography>
            About to send to <strong>{previewCount}</strong> recipient
            {previewCount === 1 ? "" : "s"}.
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 13, color: "#666" }}>
            Subject: <strong>{subject}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={doSend}
            variant="contained"
            sx={{ bgcolor: themeOrange, "&:hover": { bgcolor: "#C53F10" } }}
          >
            Send {previewCount}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DashboardEmail;
